import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import labelService from '../services/labelService';
import { getCachedTranslations, setCachedTranslations, isCacheValid } from '../services/translationCache';

/**
 * Detect if running on mobile device
 */
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};

/**
 * Custom hook to ensure translations are fully loaded before rendering
 * Prevents translation keys from being displayed instead of proper labels
 * Enhanced for mobile devices with backend translation loading
 * 
 * @param {string[]} criticalKeys - Array of critical translation keys to pre-warm
 * @returns {object} { t, i18n, ready, translationsReady, safeT }
 */
export const useTranslationReady = (criticalKeys = []) => {
  const { t, i18n, ready } = useTranslation();
  const [translationsReady, setTranslationsReady] = useState(false);
  const [backendLabelsLoaded, setBackendLabelsLoaded] = useState(false);
  const loadAttempts = useRef(0);
  const maxRetries = 3;

  // Default critical keys that most pages need
  const defaultCriticalKeys = [
    'common.save',
    'common.cancel',
    'common.edit',
    'common.delete',
    'common.search',
    'common.loading',
    'common.error',
    'common.success',
    'common.tags',
    'common.show',
    'common.hide',
    'common.yes',
    'common.no',
    'common.notAvailable'
  ];

  const allCriticalKeys = [...new Set([...defaultCriticalKeys, ...criticalKeys])];
  
  // Detect mobile and adjust delays accordingly
  const isMobile = isMobileDevice();
  // Check if cache exists - if yes, can use shorter delays
  const hasCachedTranslations = getCachedTranslations(i18n.language) !== null;
  const initialDelay = hasCachedTranslations ? 50 : (isMobile ? 300 : 150); // Much faster with cache!
  const languageChangeDelay = hasCachedTranslations ? 100 : (isMobile ? 400 : 250);

  // Load backend labels with caching and retry mechanism
  const loadBackendLabels = useCallback(async (language) => {
    // First, check if we have valid cached translations
    const cachedTranslations = getCachedTranslations(language);
    
    if (cachedTranslations && isCacheValid(language)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⚡ Using cached translations (${Object.keys(cachedTranslations).length} keys)`);
      }
      
      // Update i18n with cached translations immediately
      try {
        i18n.addResourceBundle(language, 'translation', cachedTranslations, true, true);
      } catch (error) {
        console.warn(`⚠️ Failed to update i18n with cache:`, error);
      }
      
      // Mark as loaded immediately - no backend call needed!
      setBackendLabelsLoaded(true);
      loadAttempts.current = 0;
      return;
    }
    
    // No valid cache, need to load from backend
    if (loadAttempts.current >= maxRetries) {
      console.warn(`⚠️ [useTranslationReady] Max retries reached for backend labels, using fallback`);
      setBackendLabelsLoaded(true);
      return;
    }

    try {
      loadAttempts.current++;
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 Loading translations: ${language} (attempt ${loadAttempts.current}/${maxRetries})`);
      }
      
      const labels = await labelService.getLabels(language, false);
      
      if (labels && Object.keys(labels).length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Loaded ${Object.keys(labels).length} translation keys`);
        }
        
        // Update i18n with backend translations
        try {
          i18n.addResourceBundle(language, 'translation', labels, true, true);
        } catch (error) {
          console.warn(`⚠️ Failed to update i18n:`, error);
        }
        
        // Cache the translations for next time (important for mobile!)
        setCachedTranslations(language, labels);
        
        setBackendLabelsLoaded(true);
        loadAttempts.current = 0; // Reset on success
      } else {
        throw new Error('No labels returned from backend');
      }
    } catch (error) {
      // Reduce logging noise - only log on final failure
      if (loadAttempts.current >= maxRetries) {
        console.error(`Failed to load translations after ${maxRetries} attempts:`, error.message);
      }
      
      if (loadAttempts.current < maxRetries) {
        // Retry with exponential backoff (increased delay to reduce API calls)
        const retryDelay = 1000 * loadAttempts.current; // 1s, 2s, 3s
        setTimeout(() => loadBackendLabels(language), retryDelay);
      } else {
        // Give up and use fallback
        console.warn(`⚠️ Using fallback translations`);
        setBackendLabelsLoaded(true);
      }
    }
  }, [i18n]);

  // Pre-warm critical translation keys to ensure they're loaded
  useEffect(() => {
    if (ready) {
      // Reset backend loaded state
      setBackendLabelsLoaded(false);
      loadAttempts.current = 0;
      
      // Load backend labels first
      loadBackendLabels(i18n.language);
      
      // Pre-load all critical keys
      allCriticalKeys.forEach(key => {
        try {
          t(key);
        } catch (e) {
          console.warn(`[useTranslationReady] Failed to pre-load translation key: ${key}`, e);
        }
      });

      // Add a delay to ensure all translations are fully loaded and cached
      const timer = setTimeout(() => {
        // Only mark as ready if backend labels are loaded or we've given up
        if (backendLabelsLoaded) {
          setTranslationsReady(true);
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Translations ready: ${i18n.language}`);
          }
        }
      }, initialDelay);

      return () => clearTimeout(timer);
    }
  }, [ready, i18n.language, t, allCriticalKeys.join(','), isMobile, initialDelay, loadBackendLabels, backendLabelsLoaded]);

  // Additional effect to mark ready when backend labels are loaded
  useEffect(() => {
    if (backendLabelsLoaded && ready) {
      const timer = setTimeout(() => {
        setTranslationsReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [backendLabelsLoaded, ready]);

  // Reset translation ready state when language changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🌐 Language changing to: ${i18n.language}`);
    }
    setTranslationsReady(false);
    setBackendLabelsLoaded(false);
    loadAttempts.current = 0;

    // Wait for language change to complete
    const timer = setTimeout(() => {
      setTranslationsReady(true);
    }, languageChangeDelay);

    return () => clearTimeout(timer);
  }, [i18n.language, isMobile, languageChangeDelay]);

  // Safe translation function with proper fallback
  const safeT = useCallback((key, fallback = '') => {
    // Always try to get translation, even if not marked as ready
    // This prevents showing raw keys on initial load
    const translation = t(key);

    // If translation returns the key itself, it means translation is missing
    if (translation === key) {
      // Only warn in development if translations are ready
      if (process.env.NODE_ENV === 'development' && translationsReady && ready) {
        console.warn(`Missing translation: "${key}"`);
      }
      return fallback || key;
    }

    // Return translation if found, otherwise fallback or key
    return translation || fallback || key;
  }, [t, translationsReady, ready]);

  return {
    t,
    i18n,
    ready,
    translationsReady,
    safeT
  };
};

/**
 * Loading component to show while translations are loading
 * Enhanced with mobile-specific feedback and cache status
 */
export const TranslationLoadingScreen = ({ message, language }) => {
  const isMobile = isMobileDevice();
  const hasCache = language ? isCacheValid(language) : false;
  
  let displayMessage = message;
  if (!displayMessage) {
    if (hasCache) {
      displayMessage = 'Loading from cache...';
    } else if (isMobile) {
      displayMessage = 'Loading translations... (First time may take a moment)';
    } else {
      displayMessage = 'Loading translations...';
    }
  }
  
  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300 flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{displayMessage}</p>
        {!hasCache && isMobile && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
            📱 Mobile detected - caching for faster future loads
          </p>
        )}
        {hasCache && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
            ⚡ Loading from local cache - instant!
          </p>
        )}
      </div>
    </div>
  );
};

export default useTranslationReady;

